import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, type SalesChannel } from '@/lib/api';
import { ChannelIconSelect, ChannelPill } from '@/lib/channel-icons';

const CHANNEL_COLOR_OPTIONS = ['#2563EB', '#16A34A', '#CA8A04', '#9333EA', '#E11D48', '#475569'];
const CHANNEL_CODE_PATTERN = /^[a-z0-9][a-z0-9_-]{1,62}$/;

export function ChannelsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">渠道</h2>
        <p className="text-sm text-muted-foreground mt-1">
          配置销售小组渠道，并用于用户归属、席位创建和桌面端徽章展示。
        </p>
      </div>
      <CreateChannelCard />
      <ChannelsTable />
    </div>
  );
}

function CreateChannelCard() {
  const queryClient = useQueryClient();
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: () => adminApi.listChannels({ include_inactive: true }),
  });
  const [name, setName] = useState('');
  const [codeDraft, setCodeDraft] = useState('');
  const [codeEdited, setCodeEdited] = useState(false);
  const [icon, setIcon] = useState('tags');
  const [color, setColor] = useState('#2563EB');
  const [description, setDescription] = useState('');
  const generatedCode = getGeneratedCodePreview(name, channels);
  const codeValue = codeEdited ? codeDraft : generatedCode;
  const codeInvalid = !isValidChannelCode(codeValue);
  const codeDuplicate = channels.some((channel) => channel.code === codeValue);
  // Badge always mirrors the name (not separately editable).
  const previewChannel: SalesChannel = {
    id: 'preview',
    code: codeValue,
    name: name.trim() || '渠道预览',
    badge_label: name.trim() || '渠道',
    icon,
    color,
    description: null,
    is_active: true,
    sort_order: 100,
    metadata: null,
    created_at: '',
    updated_at: '',
  };
  const create = useMutation({
    mutationFn: () =>
      adminApi.createChannel({
        code: codeValue,
        name: name.trim(),
        badge_label: name.trim(),
        icon,
        color,
        description: description.trim(),
        is_active: true,
      }),
    onSuccess: () => {
      setName('');
      setCodeDraft('');
      setCodeEdited(false);
      setIcon('tags');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      toast.success('渠道已保存');
    },
  });
  const canCreate = !!name.trim() && !codeInvalid && !codeDuplicate && !create.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">新增渠道</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-[150px_150px_220px_120px_250px_1fr_auto] md:items-end">
        <Field label="编码">
          <div className="flex items-center gap-2">
            <Input
              className="font-mono"
              value={codeValue}
              onChange={(e) => {
                setCodeEdited(true);
                setCodeDraft(cleanChannelCodeInput(e.target.value));
              }}
              aria-invalid={codeInvalid || codeDuplicate}
              title="可自定义，比如 restaurant；用于匹配 assets/industries/{code}"
            />
            {codeEdited && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                title="恢复自动编码"
                onClick={() => {
                  setCodeDraft('');
                  setCodeEdited(false);
                }}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
          {(codeInvalid || codeDuplicate) && (
            <p className="text-xs text-destructive">
              {codeDuplicate ? '编码已存在' : '仅支持小写字母、数字、下划线和短横线'}
            </p>
          )}
        </Field>
        <Field label="名称">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="房产中介" />
        </Field>
        <Field label="徽章">
          <div className="flex items-center gap-2">
            <Input value={name} disabled readOnly placeholder="同名称" title="徽章与名称一致，不可单独编辑" />
            <ChannelPill channel={previewChannel} />
          </div>
        </Field>
        <Field label="图标">
          <ChannelIconSelect value={icon} onChange={setIcon} />
        </Field>
        <Field label="颜色">
          <ChannelColorPicker value={color} onChange={setColor} />
        </Field>
        <Field label="说明">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Button disabled={!canCreate} onClick={() => create.mutate()}>
          <Plus className="h-4 w-4 mr-1" /> 新增
        </Button>
      </CardContent>
    </Card>
  );
}

function ChannelsTable() {
  const { data, isLoading } = useQuery({
    queryKey: ['channels'],
    queryFn: () => adminApi.listChannels({ include_inactive: true }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">渠道列表</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <div className="rounded border border-border">
            <Table>
              <THead>
                <TR>
                  <TH>渠道</TH>
                  <TH>徽章</TH>
                  <TH>图标</TH>
                  <TH>颜色</TH>
                  <TH>排序</TH>
                  <TH>状态</TH>
                  <TH>说明</TH>
                  <TH>操作</TH>
                </TR>
              </THead>
              <TBody>
                {data.map((channel) => (
                  <ChannelRow key={channel.id} channel={channel} channels={data} />
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChannelRow({ channel, channels }: { channel: SalesChannel; channels: SalesChannel[] }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(channel.name);
  const [code, setCode] = useState(channel.code);
  const [icon, setIcon] = useState(channel.icon || 'tags');
  const [color, setColor] = useState(channel.color);
  const [sortOrder, setSortOrder] = useState(String(channel.sort_order));
  const [description, setDescription] = useState(channel.description ?? '');
  const [isActive, setIsActive] = useState(channel.is_active);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const codeInvalid = !isValidChannelCode(code);
  const codeDuplicate = channels.some((item) => item.id !== channel.id && item.code === code);
  const update = useMutation({
    mutationFn: () =>
      adminApi.updateChannel(channel.id, {
        code,
        name: name.trim(),
        badge_label: name.trim(), // badge always mirrors the name
        icon,
        color,
        sort_order: Number(sortOrder),
        description: description.trim(),
        is_active: isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      toast.success('已保存');
    },
  });
  const remove = useMutation({
    mutationFn: () => adminApi.deleteChannel(channel.id),
    onSuccess: () => {
      setDeleteConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      toast.success('渠道已删除');
    },
  });

  const previewChannel = { ...channel, code, badge_label: name || channel.badge_label, icon, color };

  return (
    <TR>
      <TD>
        <Input className="h-8 w-36" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          className="mt-1 h-7 w-36 font-mono text-xs"
          value={code}
          aria-invalid={codeInvalid || codeDuplicate}
          onChange={(e) => setCode(cleanChannelCodeInput(e.target.value))}
          title="用于匹配 assets/industries/{code}"
        />
        {(codeInvalid || codeDuplicate) && (
          <div className="mt-1 text-[10px] text-destructive">
            {codeDuplicate ? '编码已存在' : '编码格式错误'}
          </div>
        )}
      </TD>
      <TD>
        <div className="flex items-center gap-2">
          <Input className="h-8 w-28" value={name} disabled readOnly title="徽章与名称一致，不可单独编辑" />
          <ChannelPill channel={previewChannel} />
        </div>
      </TD>
      <TD>
        <ChannelIconSelect value={icon} onChange={setIcon} triggerClassName="h-8 w-28 text-xs" />
      </TD>
      <TD>
        <ChannelColorPicker value={color} onChange={setColor} compact />
      </TD>
      <TD>
        <Input className="h-8 w-20" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </TD>
      <TD>
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={isActive} onCheckedChange={(v) => setIsActive(v === true)} />
          启用
        </label>
      </TD>
      <TD>
        <Input className="h-8 w-52" value={description} onChange={(e) => setDescription(e.target.value)} />
      </TD>
      <TD>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={!name.trim() || codeInvalid || codeDuplicate || update.isPending}
            onClick={() => update.mutate()}
          >
            <Save className="h-4 w-4 mr-1" /> 保存
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" /> 删除
          </Button>
          <Dialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            title="删除渠道"
            footer={
              <>
                <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)}>取消</Button>
                <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>确认删除</Button>
              </>
            }
          >
            <p className="text-sm text-muted-foreground">
              确认删除渠道「{channel.name}」？用户不会被删除，已有用户和席位只会清空渠道关联。
            </p>
          </Dialog>
        </div>
      </TD>
    </TR>
  );
}

function getGeneratedCodePreview(name: string, channels: SalesChannel[]): string {
  const existing = new Set(channels.map((channel) => channel.code));
  const base = slugifyCodePreview(name);
  if (base && !existing.has(base)) return base;

  for (let i = 1; i < 10_000; i += 1) {
    const candidate = base ? `${base}_${i}` : `sales_team_${i}`;
    if (!existing.has(candidate)) return candidate;
  }
  return 'sales_team';
}

function slugifyCodePreview(value: string): string | null {
  const slug = value
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48)
    .replace(/_+$/g, '');
  return /^[a-z0-9][a-z0-9_-]{1,62}$/.test(slug) ? slug : null;
}

function cleanChannelCodeInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 63);
}

function isValidChannelCode(value: string): boolean {
  return CHANNEL_CODE_PATTERN.test(value);
}

function ChannelColorPicker({
  value,
  onChange,
  compact = false,
}: {
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
}) {
  const normalizedValue = value.toUpperCase();
  const validColor = isValidHexColor(normalizedValue);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`${compact ? 'h-8 w-28' : 'h-9 w-full'} justify-between px-2 font-mono text-xs`}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span
              className="h-4 w-4 shrink-0 rounded-sm border border-border"
              style={{ backgroundColor: validColor ? normalizedValue : 'transparent' }}
            />
            <span className="truncate">{normalizedValue}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-3">
        <div className="grid grid-cols-6 gap-2">
          {CHANNEL_COLOR_OPTIONS.map((color) => {
            const selected = normalizedValue === color;
            return (
              <Button
                key={color}
                type="button"
                variant="outline"
                size="sm"
                title={color}
                className={`relative h-8 w-8 p-1 ${selected ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                onClick={() => onChange(color)}
              >
                <span className="block h-full w-full rounded-sm" style={{ backgroundColor: color }} />
                {selected && <Check className="absolute h-3.5 w-3.5 text-white drop-shadow" />}
              </Button>
            );
          })}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">取色器</Label>
          <Input
            type="color"
            className="h-9 w-full cursor-pointer p-1"
            value={validColor ? normalizedValue : '#000000'}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">HEX</Label>
          <div className="flex items-center gap-2">
            <span
              className="h-8 w-8 shrink-0 rounded-md border border-border"
              style={{ backgroundColor: validColor ? normalizedValue : 'transparent' }}
            />
            <Input
              className="h-8 font-mono text-xs uppercase"
              value={normalizedValue}
              inputMode="text"
              maxLength={7}
              onChange={(e) => onChange(cleanHexColorInput(e.target.value))}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function cleanHexColorInput(value: string): string {
  const hex = value.toUpperCase().replace(/^#/, '').replace(/[^0-9A-F]/g, '').slice(0, 6);
  return `#${hex}`;
}

function isValidHexColor(value: string): boolean {
  return /^#[0-9A-F]{6}$/.test(value);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
